import React from 'react';
import _ from 'lodash';
import cn from 'classnames';
import { getMousePosition, int, convertPxToVw, clientLog } from 'common/utils/helpers';
import { PIN, COMPONENT_STATES, COMPONENT_TYPE } from 'common/constants';
import { EventEmitter } from 'common/utils/event-emitter';
import { STORY_ELEMENTS_ID } from 'client/constants/common';
import KeyboardListener, { EventExtraData } from 'client/components/common/SelectionHint/Draggable/KeyboardListener';
import { BOX_TYPE } from 'client/components/common/BuildingBlocks/Box';
import { getSelectionPosition, getSelectionGroupPosition } from 'client/components/common/SelectionHint/utils';
import { FIELD_EDITABLE_ATTR } from 'client/components/common/BuildingBlocks/Fields/constants';
import { SelectionSingleType } from 'client/components/common/SelectionHint/types';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import AlignmentGuides, {
	AlignmentGuidesProps,
	InterestPoint,
	Intersection,
	INTERSECTION_SIDE,
} from '../AlignmentGuides';
import NearestPoint from '../NearestPoint';
import Line from '../Line';
import Point from '../Point';
import Area from '../Area';
import { SIDE, SideType, SUPPORTED_PROP } from './constants';
import css from './Points.scss';

const log = clientLog.extend('SelectionHint:Points');
const error = clientLog.extend('SelectionHint:Points');
error.log = console.error.bind(console);

const DRAG_PHASE = {
	NONE: 0,
	START: 1,
	MOVE: 2,
} as const;

const initialState = {
	shiftKey: false, // ----------> is hold shift key (used to drag by single axis on free drag)
	altKey: false, // ----------> is hold shift key (used to drag by single axis on free drag)
	shiftAxis: '' as const, // ------------> an axis to update by, if shiftKey hold
	isArrows: false, // ----------> is changing position by arrow keys
	dragging: false,
	dragPhase: DRAG_PHASE.NONE,
	x: null, // ------------------> start x axis value
	y: null, // ------------------> start y axis value
	prop: null, // ---------------> eg. start prop values { [id] : { left: 0, top: 0, right: 0, bottom: 0, ... } }
	side: '' as const, // -----------------> eg. "right"
	next: null, // { path: [], value: [] }; max next with 4 properties [top, left, width, height] on corner resize
	awaitAdminUpdate: false,
	nearestPoint: '' as const, // ----------> point nearest to mouse position
	restrictions: {
		resize: false,
		move: false,
	},
};

type State = {
	awaitAdminUpdate: boolean;
	shiftKey: boolean;
	altKey: boolean;
	shiftAxis: 'x' | 'y' | '';
	isArrows: boolean;
	dragging: boolean;
	dragPhase: typeof DRAG_PHASE[keyof typeof DRAG_PHASE];
	nearestPoint: typeof SIDE[keyof typeof SIDE] | '';
	side: typeof SIDE[keyof typeof SIDE] | '';
	x: null | number;
	y: null | number;
	prop: null | Record<
		string,
		{
			top?: string;
			right?: string;
			bottom?: string;
			left?: string;
			width?: string;
			height?: string;
		}
	>;
	next: null | {
		path: string[];
		value: string[];
	};
	restrictions: {
		resize: boolean;
		move: boolean;
	};
};

type Props = {
	selection: SelectionSingleType;
	selectionGroup: {
		[key: string]: SelectionSingleType;
	};
	name: typeof SUPPORTED_PROP[keyof typeof SUPPORTED_PROP];
	style: {
		position: string;
		top: number;
		left: number;
		width: number;
		height: number;
	};
	currentState: typeof COMPONENT_STATES[keyof typeof COMPONENT_STATES];
	currentMediaQuery: string;
	setDragging: (...args: any[]) => any;
	hidden: boolean;
	isMultiple: boolean;
	lockX: boolean;
	lockY: boolean;
	lockH: boolean;
	hiddenPoints?: SideType[];
	boundaries: {
		min?: number;
		max?: number;
	};
};

type OnMouseEventParams = {
	side: State['side']; // point side name
	prop: typeof SUPPORTED_PROP[keyof typeof SUPPORTED_PROP]; // editable supported property name
	isArrows: boolean;
	eventConfig?: {
		withScroll: boolean;
	};
};

export const PointsEvEm = new EventEmitter<'setRestrictions'>();

class Points extends React.Component<Props, State> {
	static FREE_DRAG_EVENT = 'mousedown';

	draggable: any;

	snap: any;

	intersection: Record<string, null | Record<string, Intersection>>;

	intersectionPoint: Record<string, null | Record<string, InterestPoint>>;

	constructor(props: Props, context: any) {
		super(props, context);
		this.state = initialState;
		this.draggable = null;
		this.snap = { x: null, y: null };
		this.intersection = { x: null, y: null };
		this.intersectionPoint = { x: null, y: null };
	}

	componentDidMount() {
		PointsEvEm.addListener('setRestrictions', this.setRestrictions);

		if (this.props.name === SUPPORTED_PROP.position) {
			document.addEventListener(Points.FREE_DRAG_EVENT, this.onFreeDragDown);
		}
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		const selectionChanged = this.props.selection !== prevProps.selection;
		const isPosition = this.props.name === SUPPORTED_PROP.position;

		if (this.state.restrictions.move && !prevState.restrictions.move && isPosition)
			document.removeEventListener(Points.FREE_DRAG_EVENT, this.onFreeDragDown);
		if (!this.state.restrictions.move && prevState.restrictions.move && isPosition)
			document.addEventListener(Points.FREE_DRAG_EVENT, this.onFreeDragDown);

		/*
		 Reset applied inline styles
		 ---------------------------
		 Why reset is here?
		 Before to reset inline styles and other states we have be sure that all changes transmitted to admin
		 are already included to card|story object. After admin update story|card object with a new value, client app
		 will render it again and this component will receive new selection props, but for the same node.
		 In this case we can suppose that transmitted before value is already included to story|card object.
		 */
		if (this.state.awaitAdminUpdate && selectionChanged) {
			log('Points:reset');

			const nodes = _.map(_.keys(this.state.prop), id => document.getElementById(id));
			nodes.push(this.selectedNode);

			_.forEach(this.state.next!.path, path => {
				const name = _.last(_.split(path, '.')) ?? '';
				const clear = node => (node.style[name] = ''); // eslint-disable-line
				_.forEach(nodes, clear);
			});

			// allow to select other blocks, current changes were applied
			pointerEvents.enable();

			this.setState(initialState); // eslint-disable-line react/no-did-update-set-state
		}
	}

	componentWillUnmount() {
		document.removeEventListener(Points.FREE_DRAG_EVENT, this.onFreeDragDown);
	}

	get selectedNode() {
		return document.getElementById(this.props.selection.id) as HTMLElement;
	}

	get selectedNodeCSS(): any {
		return this.selectedNode ? window.getComputedStyle(this.selectedNode) : {};
	}

	get isAllowDrag() {
		const isT = this.isPin([PIN.t]);
		const isTT = this.isPin([PIN.lt, PIN.rt, PIN.none]);
		const isR = this.isPin([PIN.r]);
		const isRR = this.isPin([PIN.rt, PIN.rb]);
		const isB = this.isPin([PIN.b]);
		const isBB = this.isPin([PIN.lb, PIN.rb]);
		const isL = this.isPin([PIN.l]);
		const isLL = this.isPin([PIN.lt, PIN.lb, PIN.none]);
		const isC = this.isPin([PIN.c]);

		return {
			top: isTT || isC || isT || isR || isL,
			right: isRR || isR,
			bottom: isBB || isB,
			left: isLL || isC || isT || isL || isB,
		};
	}

	/**
	 * Get allowed side when was clicked an opposite
	 *
	 * @description
	 * Used for position drag.
	 * Need to update only allowed position property accordingly to current "pin", if was clicked an opposite side.
	 * @return {string}
	 */
	get allowedSide() {
		let { side } = this.state;

		if (this.isAllowDrag.left && this.state.side === SIDE.right) {
			side = SIDE.left;
		}
		if (this.isAllowDrag.right && this.state.side === SIDE.left) {
			side = SIDE.left;
		}
		if (this.isAllowDrag.top && this.state.side === SIDE.bottom) {
			side = SIDE.top;
		}
		if (this.isAllowDrag.bottom && this.state.side === SIDE.top) {
			side = SIDE.top;
		}

		return side;
	}

	// editable property name
	get propertyName() {
		let name: string = '';

		if (this.props.name === SUPPORTED_PROP.padding) {
			name = _.camelCase(`${this.props.name}-${this.state.side}`);
		}

		if (this.props.name === SUPPORTED_PROP.position) {
			name = this.allowedSide;
		}

		if (this.props.name === SUPPORTED_PROP.size) {
			if ([SIDE.left, SIDE.right].includes(this.state.side as 'left' | 'right')) {
				name = 'width';
			} else {
				name = 'height';
			}
		}

		return name;
	}

	get axis() {
		return ([SIDE.left, SIDE.right] as any[]).includes(this.state.side) ? 'x' : 'y';
	}

	get pin() {
		const defaultPin = PIN.t;
		return _.get(this.selectedNode, 'dataset.pin', defaultPin);
	}

	getPathToUpdate = (propertyName: string, alternativePath?: string) => {
		const { currentState, currentMediaQuery } = this.props;
		const path = alternativePath || this.selectedNode.dataset.path;

		return `elements.${path}.uiConfig.componentProps.styles.${currentState}.${currentMediaQuery}.${propertyName}`;
	};

	setRestrictions = (event: { type: 'setRestrictions'; data: Partial<State['restrictions']> }) => {
		this.setState(ps => ({ restrictions: { ...ps.restrictions, ...event.data } }));
	};

	isPin = (pin: typeof PIN[keyof typeof PIN][]) => pin.includes(this.pin);

	/**
	 * Store values
	 */
	transmitToAdmin = () => {
		log('Points:transmitToAdmin');
		if (!this.state.next) return;

		const storyElements = document.getElementById(STORY_ELEMENTS_ID);
		const isGlobal = storyElements!.contains(this.selectedNode);

		transmitToAdminBbUpdate({
			id: Points.name,
			path: this.state.next.path,
			value: this.state.next.value.map(v => convertPxToVw(v)),
			isStory: isGlobal,
		});
	};

	/**
	 * @param nearestPoint {String} nearest point name (known as "SIDE")
	 */
	onNearestPoint = (nearestPoint: State['nearestPoint']) => {
		if (nearestPoint === this.state.nearestPoint) return;
		this.setState({ nearestPoint });
	};

	onKeyDown = async (event: KeyboardEvent, params: EventExtraData) => {
		const isArrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(params.code);
		const isSpaceKey = params.code === 'Space';

		if (isArrowKeys || isSpaceKey) {
			event.preventDefault();
		}

		if (this.state.awaitAdminUpdate) {
			error('Too fast. Values are not yet updated after last *Up event');
			return;
		}

		if (this.props.name === SUPPORTED_PROP.position) {
			if (isArrowKeys) {
				// prevent another block selection until current changes won't be applied
				pointerEvents.disable(this.props.selection.uiConfig.nodeProps.id);

				const increment = params.shift ? 10 : 1;

				let side: typeof SIDE[keyof typeof SIDE] = SIDE.region;
				if (!this.props.isMultiple) {
					side = params.code === 'ArrowLeft' || params.code === 'ArrowRight' ? SIDE.left : SIDE.top;
				}

				const _params = {
					side,
					prop: SUPPORTED_PROP.position,
					eventConfig: {
						withScroll: false,
					},
					isArrows: true,
				};

				const mouseDownEvent = {
					target: null,
					clientX: this.props.style.left,
					clientY: this.props.style.top,
				};
				await this.onMouseDown(mouseDownEvent, _params);

				const mouseMoveEvent = {
					target: null,
					clientX:
						params.code === 'ArrowLeft' || params.code === 'ArrowRight'
							? this.state.x! + (params.code === 'ArrowLeft' ? -increment : increment)
							: this.state.x,
					clientY:
						params.code === 'ArrowUp' || params.code === 'ArrowDown'
							? this.state.y! + (params.code === 'ArrowUp' ? -increment : increment)
							: this.state.y,
				};
				await this.onMouseMove(mouseMoveEvent, _params);
			} else if (['ShiftLeft', 'ShiftRight'].includes(params.code) && !this.state.shiftKey) {
				this.setState({ shiftKey: true, shiftAxis: 'x' });
			} else if (params.alt) {
				this.setState({ dragPhase: DRAG_PHASE.MOVE, altKey: true });
			}
		}
	};

	onKeyUp = (event: KeyboardEvent, params: EventExtraData) => {
		if (this.props.name === SUPPORTED_PROP.position) {
			const shiftKeys = ['ShiftLeft', 'ShiftRight'];
			const altKeys = ['AltLeft', 'AltRight'];

			if (this.state.isArrows && this.state.dragging) {
				event.preventDefault();
				this.onMouseUp({});
			} else if (shiftKeys.includes(params.code) && this.state.shiftKey) {
				this.setState(_.pick(initialState, ['shiftKey', 'shiftAxis']));
			} else if (altKeys.includes(params.code) && this.state.altKey) {
				this.setState(initialState);
			}
		}
	};

	// eslint-disable-next-line react/sort-comp
	debouncedOnKeyUp = _.debounce(this.onKeyUp, 350);

	onMouseDown = (
		event: React.MouseEvent<HTMLElement> | { target: null; clientX: number; clientY: number },
		params: OnMouseEventParams
	) => {
		if (
			// prevent custom keyboard shortcuts and changing position of BB while it is in editable mode
			params.prop === SUPPORTED_PROP.position &&
			(this.selectedNode.contentEditable === 'true' || this.selectedNode.hasAttribute(FIELD_EDITABLE_ATTR))
		) {
			return false;
		}

		if (this.props.lockX && this.props.lockY) {
			return false;
		}

		const T = event.target;
		const M = getMousePosition(event, { by: 'client', withScroll: false, ...params.eventConfig });
		const S = window.getComputedStyle(this.selectedNode);

		const editableProp = {};
		if (params.prop === SUPPORTED_PROP.position) {
			_.forOwn(this.props.selectionGroup, selection => {
				const selectedNode = document.getElementById(selection.id);
				if (selectedNode) {
					const style = window.getComputedStyle(selectedNode);
					editableProp[selection.id] = { top: style.top, left: style.left };
				}
			});
		}

		if (params.prop === SUPPORTED_PROP.padding) {
			editableProp[Object.keys(this.props.selectionGroup)[0]] = {
				top: S.paddingTop,
				right: S.paddingRight,
				bottom: S.paddingBottom,
				left: S.paddingLeft,
			};
		}

		if (params.prop === SUPPORTED_PROP.size) {
			editableProp[Object.keys(this.props.selectionGroup)[0]] = {
				width: S.width,
				height: S.height,
				top: S.top,
				left: S.left,
			};
		}

		return this.setState(
			{
				dragging: true,
				dragPhase: params.isArrows ? DRAG_PHASE.MOVE : DRAG_PHASE.START,
				x: M.x,
				y: M.y,
				prop: editableProp,
				side: params.side,
				isArrows: params.isArrows,
			},
			() => {
				if (T) {
					this.draggable = T;
					document.addEventListener('mouseup', this.onMouseUp);
					document.addEventListener('mousemove', this.onMouseMove);
				}
			}
		);
	};

	onMouseUp = event => {
		if (this.draggable) {
			this.draggable = null;
			document.removeEventListener('mouseup', this.onMouseUp);
			document.removeEventListener('mousemove', this.onMouseMove);
		}
		this.props.setDragging('');
		if (this.state.next) {
			this.transmitToAdmin();
			this.setState({ awaitAdminUpdate: true });
		} else {
			this.setState(initialState);
		}
	};

	/**
	 * @param event {MouseEvent|Object} Usually real event, but in case of keydown it going to be a fake event object
	 * @param params {Object}
	 * @param params.eventConfig {Object}
	 */
	onMouseMove = (event, params: Partial<OnMouseEventParams> = {}) => {
		if (!this.state.dragging) return;

		if (!this.state.next) {
			// update SelectionHint (breadcrumbs, selection outline view ...)
			this.props.setDragging(this.props.name, this.props.selection.id);
		}

		const mouse = getMousePosition(event, { by: 'client', withScroll: false, ...params.eventConfig });

		if (this.props.name === SUPPORTED_PROP.padding || this.props.name === SUPPORTED_PROP.position) {
			this.changePosition({ mouse });
		}
		if (this.props.name === SUPPORTED_PROP.size) {
			this.changeSize({ mouse });
		}
	};

	onFreeDragDown = event => {
		log('onFreeDragDown');
		if (event.button !== 0 || event.buttons === 0) {
			event.preventDefault();
			return;
		}

		const some = fn => _.some(_.keys(this.props.selectionGroup), id => fn(document.getElementById(id)));
		const isTargetSelected = some(node => event.target === node);
		const isSelectedContendEditable = some(
			node => node.getAttribute('contenteditable') === 'true' || event.target.hasAttribute(FIELD_EDITABLE_ATTR)
		);
		const isSelectedContainsTarget = some(node => node.contains(event.target));

		if ((!isTargetSelected && !isSelectedContainsTarget) || isSelectedContendEditable) {
			log('onFreeDragDown: <-- exit', { isTargetSelected, isSelectedContainsTarget, isSelectedContendEditable });
			return;
		}

		log('onFreeDragDown --> this.onMouseDown');
		this.onMouseDown(event, { side: SIDE.region, prop: SUPPORTED_PROP.position, isArrows: false });
	};

	changePosition = ({ mouse }: { mouse: ReturnType<typeof getMousePosition> }) => {
		const D = {
			x: mouse.x - (this.state.x || 0),
			y: mouse.y - (this.state.y || 0),
		};
		/*
		       -
		       |
		 - ––––|–––– +
		       |
		       +
		 */

		if (this.props.lockX && D.x !== 0) {
			D.x = 0;
		}

		if (this.props.lockY && D.y !== 0) {
			D.y = 0;
		}

		if ((this.intersection.x || this.intersection.y) && !this.state.isArrows) {
			const snapParams = this.state.side === SIDE.top ? { side: INTERSECTION_SIDE.start } : undefined;
			const snap = this.snapDeltaToIntersection(D, snapParams);
			D.x = snap.x;
			D.y = snap.y;
		}

		// Drag freely for a pinned block
		if (this.state.side === SIDE.region) {
			const { shiftAxis, shiftKey } = this.state;

			if (shiftKey) {
				const nextShiftAxis = Math.abs(D.x) >= Math.abs(D.y) ? 'x' : 'y';
				const nextShiftAxisOpposite = nextShiftAxis === 'x' ? 'y' : 'x';

				D[nextShiftAxisOpposite] = 0;

				if (shiftAxis !== nextShiftAxis) {
					this.setState({ shiftAxis: nextShiftAxis });
				}
			}

			if (shiftKey && !shiftAxis) {
				return;
			}

			const next: { path: string[]; value: any[] } = {
				path: [],
				value: [],
			};

			_.forOwn(this.props.selectionGroup, selection => {
				const selectedNode = document.getElementById(selection.id) as HTMLElement;
				const prop = this.state.prop?.[selection.id];

				const xFactor = 1;
				const xProp = SIDE.left;
				const xValue = `${int(prop?.[xProp]) + D.x * xFactor}px`;
				selectedNode.style[xProp] = xValue;

				const yFactor = 1;
				const yProp = SIDE.top;
				const yValue = `${int(prop?.[yProp]) + D.y * yFactor}px`;
				selectedNode.style[yProp] = yValue;

				const { path } = selectedNode.dataset;

				next.path.push(this.getPathToUpdate(xProp, path), this.getPathToUpdate(yProp, path));
				next.value.push(xValue, yValue);
			});

			this.setState({ next, dragPhase: DRAG_PHASE.MOVE });

			return;
		}

		// Drag by single side
		// Enabled drag only for a SIDE.top in case of dragging unpinned relative block or by arrow keys.
		// To enable drag by any side: remove "visibility: hidden" assigned to .points-position
		const factor =
			([SIDE.right, SIDE.bottom] as any[]).includes(this.allowedSide) &&
			(this.isAllowDrag.right || this.isAllowDrag.bottom)
				? -1
				: 1;
		const prop = Object.values(this.state.prop || {})[0];
		const startPropValue = int(prop[this.allowedSide]);
		const nextPropsNumber = startPropValue + D[this.axis] * factor;
		const nextPropValue = `${nextPropsNumber}px`;

		if (this.props.name === SUPPORTED_PROP.padding || this.props.name === SUPPORTED_PROP.position) {
			this.selectedNode.style[this.propertyName] = nextPropValue;
		}

		this.setState(prevState => {
			const next = {
				path: [this.getPathToUpdate(this.propertyName)],
				value: [nextPropValue],
			};

			if (prevState.next) {
				// If you made quick movements with arrows in different directions,
				// you must also store data about movements in other directions
				let index = prevState.next.path.indexOf(next.path[0]);
				if (index === -1) {
					index = prevState.next.path.length;
				}
				const path = [...prevState.next.path];
				const value = [...prevState.next.value];
				_.set(path, index, next.path[0]);
				_.set(value, index, next.value[0]);

				return { next: { path, value }, dragPhase: prevState.dragPhase };
			}

			return { next, dragPhase: DRAG_PHASE.MOVE };
		});
	};

	changeSize = ({ mouse }: { mouse: ReturnType<typeof getMousePosition> }) => {
		this.setState(prevState => {
			const D = {
				x: mouse.x - (prevState.x || 0),
				y: mouse.y - (prevState.y || 0),
			};
			/*
			       -
			       |
			 - ––––|–––– +
			       |
			       +
			 */
			if (this.intersection.x || this.intersection.y) {
				const snapParams = {
					side: ([SIDE.right, SIDE.bottom, SIDE.rightBottom, SIDE.leftBottom] as any[]).includes(
						prevState.side
					)
						? INTERSECTION_SIDE.end
						: INTERSECTION_SIDE.start,
				};
				const snap = this.snapDeltaToIntersection(D, snapParams);
				D.x = snap.x;
				D.y = snap.y;
			}

			if (!prevState.prop) {
				return null;
			}

			const next: { path: string[]; value: any[] } = {
				path: [],
				value: [],
			};
			// log('Points:changeSize', 'axis: ', axis, 'delta: ', D, 'prop: ', prevState.prop);

			let widthFactor = [SIDE.leftTop, SIDE.leftBottom, SIDE.left].some(side => prevState.side === side) ? -1 : 1;
			let heightFactor = [SIDE.leftTop, SIDE.rightTop, SIDE.top].some(side => prevState.side === side) ? -1 : 1;
			let leftFactor = 1;

			const { type, uiConfig } = this.props.selection;
			const isFrame = type === COMPONENT_TYPE.BOX && uiConfig.componentProps.other.boxType === BOX_TYPE.FRAME;
			const preserveAspectRatio =
				([SIDE.leftTop, SIDE.rightTop, SIDE.rightBottom, SIDE.leftBottom] as any[]).includes(prevState.side) &&
				!isFrame &&
				!(
					[
						COMPONENT_TYPE.SHAPE,
						COMPONENT_TYPE.RESULT_SHAPE,
						COMPONENT_TYPE.TEXT_AREA_FIELD,
						COMPONENT_TYPE.TEXT_FIELD,
					] as string[]
				).includes(type);

			// next code block cares only about proportional resize by corner points
			if (preserveAspectRatio) {
				const prop = Object.values(prevState.prop)[0];
				const ratio = int(prop.width) / int(prop.height);
				D.x = D.y * ratio;

				if (SIDE.leftTop === prevState.side) {
					heightFactor = -1;
					widthFactor = -1;
				} else if (SIDE.rightTop === prevState.side) {
					heightFactor = -1;
					widthFactor = -1;
					leftFactor = -1;
				} else if (SIDE.rightBottom === prevState.side) {
					heightFactor = 1;
					widthFactor = 1;
				} else if (SIDE.leftBottom === prevState.side) {
					heightFactor = 1;
					widthFactor = 1;
					leftFactor = -1;
				}
			}

			const shouldUpdateW = (
				[SIDE.left, SIDE.right, SIDE.leftTop, SIDE.rightTop, SIDE.leftBottom, SIDE.rightBottom] as any[]
			).includes(prevState.side);
			const shouldUpdateH = (
				[SIDE.top, SIDE.bottom, SIDE.leftTop, SIDE.rightTop, SIDE.leftBottom, SIDE.rightBottom] as any[]
			).includes(prevState.side);

			let shouldUpdateL;
			let shouldUpdateT;
			let isMiddleX;
			let isMiddleY;
			const isSide = (arr: string[] = []) =>
				prevState.side !== '' && _.values(_.pick(SIDE, arr)).includes(prevState.side);

			// top
			if ([PIN.lt, PIN.t, PIN.rt, PIN.none].includes(this.pin)) {
				shouldUpdateT = isSide(['top', 'leftTop', 'rightTop']);
			}

			// bottom
			if ([PIN.lb, PIN.b, PIN.rb].includes(this.pin)) {
				shouldUpdateT = isSide(['bottom', 'leftBottom', 'rightBottom']);
				isMiddleY = isSide([SIDE.top]) && this.pin === PIN.b;
			}

			// left
			if ([PIN.lt, PIN.l, PIN.lb, PIN.none].includes(this.pin)) {
				shouldUpdateL = isSide(['left', 'leftTop', 'leftBottom']);
			}

			// right
			if ([PIN.r, PIN.rt, PIN.rb].includes(this.pin)) {
				shouldUpdateL = isSide(['right', 'rightTop', 'rightBottom']);
			}

			// x center
			if ([PIN.t, PIN.c, PIN.b].includes(this.pin)) {
				shouldUpdateL = isSide(['left', 'leftTop', 'leftBottom', 'right', 'rightTop', 'rightBottom']);
				isMiddleX = isSide(['left', 'leftTop', 'leftBottom', 'right', 'rightTop', 'rightBottom']);
			}

			// y center
			if ([PIN.l, PIN.c, PIN.r].includes(this.pin)) {
				shouldUpdateT = isSide(['top', 'bottom', 'leftTop', 'leftBottom', 'rightTop', 'rightBottom']);
				isMiddleY = isSide(['top', 'bottom', 'leftTop', 'leftBottom', 'rightTop', 'rightBottom']);
			}

			const incMiddleX = isMiddleX ? 0.5 : 1;
			const incMiddleY = isMiddleY ? 0.5 : 1;

			const prop = Object.values(prevState.prop || {})[0];

			// Limit
			const limitRatio = window.innerWidth / (this.props.boundaries.min ?? 1);
			const minWidth = Math.ceil(10 * limitRatio);
			const minHeight = Math.ceil(10 * limitRatio);

			const _widthNum = int(prop.width) + D.x * widthFactor;
			if (_widthNum < minHeight) {
				if (widthFactor === -1) {
					D.x -= minWidth - _widthNum;
				} else {
					D.x += minWidth - _widthNum;
				}
			}

			const _heightNum = int(prop.height) + D.y * heightFactor;
			if (_heightNum < minHeight) {
				if (heightFactor === -1) {
					D.y -= minHeight - _heightNum;
				} else {
					D.y += minHeight - _heightNum;
				}
			}
			// Limit end

			const widthNum = int(prop.width) + D.x * widthFactor;
			const width = shouldUpdateW ? `${widthNum}px` : undefined;

			const heightNum = int(prop.height) + D.y * heightFactor;
			const height = shouldUpdateH ? `${heightNum}px` : undefined;

			const positionLeftNum = int(prop.left) + D.x * incMiddleX * leftFactor;
			const positionLeft = shouldUpdateL ? `${positionLeftNum}px` : undefined;

			const positionTopNum = int(prop.top) + D.y * incMiddleY;
			const positionTop = shouldUpdateT ? `${positionTopNum}px` : undefined;

			this.selectedNode.style.width = `${width}`;
			this.selectedNode.style.left = `${positionLeft}`;

			this.selectedNode.style.height = `${height}`;
			this.selectedNode.style.top = `${positionTop}`;

			if (!_.isUndefined(width)) {
				next.path.push(this.getPathToUpdate('width'));
				next.value.push(width);
			}
			if (!_.isUndefined(height)) {
				next.path.push(this.getPathToUpdate('height'));
				next.value.push(height);
			}
			if (!_.isUndefined(positionTop)) {
				next.path.push(this.getPathToUpdate('top'));
				next.value.push(positionTop);
			}

			if (!_.isUndefined(positionLeft)) {
				next.path.push(this.getPathToUpdate('left'));
				next.value.push(positionLeft);
			}

			if (!next.path.length && !next.value.length) {
				error('Points:changeSize — next is empty');
				return null;
			}

			return { next, dragPhase: DRAG_PHASE.MOVE };
		});
	};

	renderPoints = () => {
		const { left, top } = this.selectedNodeCSS;
		const { paddingLeft, paddingRight, paddingTop, paddingBottom } = this.selectedNodeCSS;
		const { width, height } = this.selectedNodeCSS;
		const { position, padding, size } = SUPPORTED_PROP;
		const { lockX, lockY, lockH } = this.props;
		const isShowAreas = this.state.dragPhase === DRAG_PHASE.MOVE;
		const activeSide = this.state.side;

		if (this.props.name === size) {
			const points = _.values(_.omit(SIDE, [SIDE.region]));
			const areas = [
				{ side: SIDE.top, enabled: activeSide === SIDE.top, property: 'size', value: height },
				{ side: SIDE.right, enabled: activeSide === SIDE.right, property: 'size', value: width },
				{ side: SIDE.bottom, enabled: activeSide === SIDE.bottom, property: 'size', value: height },
				{ side: SIDE.left, enabled: activeSide === SIDE.left, property: 'size', value: width },
				{
					side: SIDE.region,
					enabled: [SIDE.leftTop, SIDE.leftBottom, SIDE.rightTop, SIDE.rightBottom].includes(
						activeSide as any
					),
					property: [SIDE.leftBottom, SIDE.rightTop].includes(activeSide as any) ? 'size-bt' : 'size-tb',
					value: [width, height],
				},
			];

			const isLockSide = side =>
				(lockX && ![SIDE.top, SIDE.bottom].includes(side)) ||
				(lockY && ![SIDE.left, SIDE.right].includes(side)) ||
				(lockH && ![SIDE.left, SIDE.right].includes(side)) ||
				this.props.hiddenPoints?.some(p => p === side);

			return (
				<div className={css.pointsSize}>
					{points.map(side => (
						<Point
							key={`size-point-${side}`}
							side={side}
							prop={size}
							onMouseDown={this.onMouseDown}
							active={this.state.nearestPoint === side && !isLockSide(side)}
						/>
					))}

					{isShowAreas && (
						<div className={css.pointsAreas}>
							{areas.map(
								({ side, enabled, property, value }) =>
									enabled && (
										<Area
											key={`size-area-${side}`}
											property={property}
											value={value}
											className={cn(css.area, css[`area-${side}`])}
											side={side}
										/>
									)
							)}
						</div>
					)}
				</div>
			);
		}

		if (this.props.name === position) {
			const points = [
				{ side: SIDE.top, enabled: true },
				{ side: SIDE.right, enabled: true },
				{ side: SIDE.bottom, enabled: true },
				{ side: SIDE.left, enabled: true },
				{ side: SIDE.region, enabled: activeSide === SIDE.region && this.state.next },
			];
			const areas = [
				{
					side: SIDE.top,
					enabled: this.isAllowDrag.top && ![SIDE.left, SIDE.right].includes(activeSide as any) && !lockY,
					property: 'top',
					value: top,
				},
				{
					side: SIDE.right,
					enabled: this.isAllowDrag.right && ![SIDE.top, SIDE.bottom].includes(activeSide as any) && !lockX,
					property: 'right',
					value: left,
				},
				{
					side: SIDE.bottom,
					enabled: this.isAllowDrag.bottom && ![SIDE.left, SIDE.right].includes(activeSide as any) && !lockY,
					property: 'bottom',
					value: top,
				},
				{
					side: SIDE.left,
					enabled: this.isAllowDrag.left && ![SIDE.top, SIDE.bottom].includes(activeSide as any) && !lockX,
					property: 'left',
					value: left,
				},
			];

			return (
				<div className={css.pointsPosition}>
					{points.map(
						({ side, enabled }) =>
							enabled && (
								<Point
									key={`position-point-${side}`}
									side={side}
									prop={position}
									onMouseDown={this.onMouseDown}
									active={this.state.nearestPoint === side || side === SIDE.region}
								/>
							)
					)}

					{isShowAreas && (
						<div className={css.pointsAreas}>
							{areas.map(
								({ side, enabled, property, value }) =>
									enabled && (
										<Area
											key={`position-area-${side}`}
											property={property}
											value={value}
											className={cn(css.area, css[`area-${side}`])}
											side={side}
										/>
									)
							)}
						</div>
					)}
				</div>
			);
		}

		if (this.props.name === padding) {
			return (
				<div className={css.pointsPadding}>
					<Point onMouseDown={this.onMouseDown} side={SIDE.top} prop={padding} />
					<Point onMouseDown={this.onMouseDown} side={SIDE.right} prop={padding} />
					<Point onMouseDown={this.onMouseDown} side={SIDE.bottom} prop={padding} />
					<Point onMouseDown={this.onMouseDown} side={SIDE.left} prop={padding} />
					{isShowAreas && (
						<div className={css.pointsAreas}>
							<Area className={css.areaTop} property="padding-top" value={paddingTop} />
							<Area className={css.areaRight} property="padding-right" value={paddingRight} />
							<Area className={css.areaBottom} property="padding-bottom" value={paddingBottom} />
							<Area className={css.areaLeft} property="padding-left" value={paddingLeft} />
						</div>
					)}
				</div>
			);
		}

		return null;
	};

	onIntersection: AlignmentGuidesProps['onIntersection'] = (intersection, intersectionPoint) => {
		_.set(this.intersection, `${intersectionPoint.axis}.${intersectionPoint.id}`, intersection);
		_.set(this.intersectionPoint, `${intersectionPoint.axis}.${intersectionPoint.id}`, intersectionPoint);
	};

	/**
	 * @param delta {Object}
	 * @param delta.x {Number}
	 * @param delta.y {Number}
	 * @param params {Object}
	 * @param params.side {String<start|end>} used on resize
	 *        in case of multiple x or y intersections to snap only to that side which user moves
	 */
	snapDeltaToIntersection = (
		delta: { x: number; y: number },
		params: { side?: typeof INTERSECTION_SIDE[keyof typeof INTERSECTION_SIDE] } = {}
	) => {
		const nextDelta = { ...delta };

		_.forOwn(this.intersection, (intersections, axis) => {
			if (!intersections) {
				return;
			}

			_.forOwn(intersections, (intersection, id) => {
				if (!intersection || !intersection.result) {
					return;
				}

				const [intersectionSide] = Object.entries(intersection.result).filter(entry => entry[1])[0];

				const snapD = delta[axis] - intersection.diff[intersectionSide];
				const snapDMin = snapD - intersection.tolerance;
				const snapDMax = snapD + intersection.tolerance;

				if (!_.get(this.snap, [axis, id])) {
					_.set(this.snap, [axis, id], {
						snapD,
						snapDMin,
						snapDMax,
					});
				} else if (
					snapD > _.get(this.snap, [axis, id, 'snapDMax']) ||
					snapD < _.get(this.snap, [axis, id, 'snapDMin'])
				) {
					_.unset(this.snap, [axis, id]);
					_.unset(this.intersection, [axis, id]);
					_.unset(this.intersectionPoint, [axis, id]);
				}

				const snap = _.get(this.snap, [axis, id]);
				const cond1 = snap && !params.side;
				const cond2 = snap && params.side === intersectionSide;
				if (cond1 || cond2) {
					nextDelta[axis] = snap.snapD;
				}
			});
		});

		return nextDelta;
	};

	render() {
		if (
			this.props.hidden ||
			!this.selectedNode ||
			!this.selectedNode.parentNode ||
			(this.state.restrictions.move && this.props.name === SUPPORTED_PROP.position) ||
			(this.state.restrictions.resize && this.props.name === SUPPORTED_PROP.size)
		) {
			return null;
		}

		const { dragging } = this.state;
		const isPosition = this.props.name === SUPPORTED_PROP.position;
		const node = this.selectedNode;

		let { style } = this.props;
		if (dragging) {
			// @ts-expect-error ts-migrate FIXME
			style = this.props.selectionGroup
				? getSelectionGroupPosition(this.props.selectionGroup)
				: getSelectionPosition(node);
		}

		const isGuideX = dragging && ([SIDE.left, SIDE.right] as any[]).includes(this.state.side) && isPosition;
		const isGuideY = dragging && ([SIDE.top, SIDE.bottom] as any[]).includes(this.state.side) && isPosition;
		const isGuideO = (!dragging || SIDE.region === this.state.side) && isPosition;

		return (
			<>
				{isGuideO && <Line pin={this.pin} selectionStyle={style} axis={Line.axis.origin} node={node} />}
				{isGuideX && <Line pin={this.pin} selectionStyle={style} axis={Line.axis.x} node={node} />}
				{isGuideY && <Line pin={this.pin} selectionStyle={style} axis={Line.axis.y} node={node} />}
				<div
					className={cn(css.points, { [css.pointsDragging]: dragging || this.state.altKey })}
					style={style as any}
				>
					<KeyboardListener
						onKeyDown={this.onKeyDown}
						onKeyUp={this.state.altKey ? this.onKeyUp : this.debouncedOnKeyUp}
					/>
					{!dragging && <NearestPoint selectionCoords={style} onNearestPoint={this.onNearestPoint} />}
					{this.renderPoints()}
				</div>
				{this.state.next && (
					<>
						<div className={css.overlay} />
						<AlignmentGuides selection={this.props.selection} onIntersection={this.onIntersection} />
					</>
				)}
			</>
		);
	}
}

/**
 * Why this is used?
 * To prevent some particular issue. Here is a steps to reproduce an issue:
 *      1. select block A
 *      2. move A with an arrow keys
 *      3. defer(_.debounce) block A changes
 *      4. select block B (client + admin)
 *      5. apply debounced block A changes,
 *        BUT there is a problem, admin tries to apply received block A changes, but have selected a block B already,
 *        so this is why block A changes won't be applied (see IframeMessageReceiver).
 *        Current solution blocks selection of block B, until block A changes won't be saved
 */
const pointerEvents = {
	id: 'pointer-events-blocker',
	disable(selectedId: string) {
		if (selectedId && !document.getElementById(this.id)) {
			const style = document.createElement('style');
			style.id = this.id;
			style.innerText = `body *:not(#${selectedId}) { pointer-events: none !important; }`;
			document.body.append(style);
		}
	},
	enable() {
		const busyStyle = document.getElementById(this.id);
		if (busyStyle) busyStyle.remove();
	},
};

export default Points;
